import React, { useMemo, useState } from 'react'
import { X, Package, Camera, MapPin } from 'lucide-react'
import { useUser } from '../contexts/UserContext'

interface DonationListingFormProps {
  onClose: () => void
  onSubmit: (formData: DonationListingData) => void
  isSubmitting?: boolean
  userType?: 'SUPPLIER' | 'FOOD_BANK' | 'VOLUNTEER'
}

export interface DonationListingData {
  title: string
  description: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  expirationDate: string
  specialInstructions: string
  contactName: string
  contactPhone: string
  foodCategories: string[]
  allergenInfo: string
  storageRequirements: string
  imageUrl?: string
  /** Local file selected for upload (not sent as JSON; uploaded separately) */
  photoFile?: File | null
  pickupStreet: string
  pickupCity: string
  pickupState: string
  pickupZipCode: string
  pickupCountry?: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  pickupTimeRange?: string
  items?: any[]
}

const DonationListingForm: React.FC<DonationListingFormProps> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
  userType = 'SUPPLIER'
}) => {
  const { user } = useUser()
  const [formData, setFormData] = useState<DonationListingData>({
    title: '',
    description: '',
    estimatedWeight: '',
    urgency: 'MEDIUM',
    expirationDate: '',
    specialInstructions: '',
    contactName: '',
    contactPhone: '',
    foodCategories: [],
    allergenInfo: '',
    storageRequirements: '',
    imageUrl: '',
    photoFile: null,
    pickupStreet: '',
    pickupCity: '',
    pickupState: '',
    pickupZipCode: '',
    pickupCountry: 'USA',
    items: []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const supplierAddress = useMemo(() => {
    if (!user) return ''
    return [user.street, user.city, user.state, user.zipCode].filter(Boolean).join(', ')
  }, [user])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setFormData(prev => ({ ...prev, photoFile: file, imageUrl: '' }))
    setPhotoPreview(file ? URL.createObjectURL(file) : null)
    if (errors.photoFile) {
      setErrors(prev => {
        const next = { ...prev }
        delete next.photoFile
        return next
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (!formData.description.trim()) newErrors.description = 'Description is required'
    if (!formData.estimatedWeight.trim()) newErrors.estimatedWeight = 'Estimated weight/quantity is required'

    if (userType === 'SUPPLIER') {
      if (!user?.street || !user?.city || !user?.state || !user?.zipCode) {
        newErrors.pickupStreet =
          'Your business address is missing. Complete onboarding before creating donations.'
      }
      const phone = user?.phone?.trim() || ''
      if (!phone || phone.startsWith('__pending__')) {
        newErrors.contactPhone =
          'Your business phone is missing. Complete onboarding before creating donations.'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    if (userType === 'SUPPLIER' && user) {
      const contactName =
        user.organizationName?.trim() ||
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        'Supplier Contact'

      onSubmit({
        ...formData,
        contactName,
        contactPhone: user.phone.trim(),
        pickupStreet: user.street || '',
        pickupCity: user.city || '',
        pickupState: user.state || '',
        pickupZipCode: user.zipCode || '',
        pickupCountry: 'USA',
        specialInstructions: '',
        foodCategories: [],
        urgency: 'MEDIUM',
        scheduledDate: undefined,
        pickupTime: undefined,
        pickupTimeRange: undefined
      })
      return
    }

    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Donation</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create a donation listing for your surplus food
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            </div>

            <div>
              <label htmlFor="title" className="form-label">
                Donation Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`input-field ${errors.title ? 'border-red-500' : ''}`}
                placeholder="e.g., Fresh bakery items donation"
                required
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="estimatedWeight" className="form-label">
                Estimated Weight/Quantity *
              </label>
              <input
                type="text"
                id="estimatedWeight"
                name="estimatedWeight"
                value={formData.estimatedWeight}
                onChange={handleChange}
                className={`input-field ${errors.estimatedWeight ? 'border-red-500' : ''}`}
                placeholder="e.g., 20 lbs, 5 boxes, 30 items"
                required
              />
              {errors.estimatedWeight && (
                <p className="text-red-500 text-sm mt-1">{errors.estimatedWeight}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="description" className="form-label">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={5}
                value={formData.description}
                onChange={handleChange}
                className={`input-field ${errors.description ? 'border-red-500' : ''}`}
                placeholder="Describe what you’re donating, expiration/best-by date, alternative pickup location or hours, and any other special instructions for volunteers."
                required
              />
              <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Include in this description: food expiration / best-by date, alternative pickup location or
                hours (if different from your business address), and any other special instructions for
                volunteers.
              </p>
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Pickup Location</h3>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <p className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-secondary-600 shrink-0" />
                <span>
                  Pickup uses your business address
                  {supplierAddress ? (
                    <>
                      : <span className="font-medium text-gray-900">{supplierAddress}</span>
                    </>
                  ) : (
                    <> (complete onboarding to set your address).</>
                  )}
                </span>
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Contact on the listing uses your supplier profile name and phone. Note alternate pickup
                details in the description if needed.
              </p>
            </div>
            {errors.pickupStreet && (
              <p className="text-red-500 text-sm mt-2">{errors.pickupStreet}</p>
            )}
            {errors.contactPhone && (
              <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>
            )}
          </div>

          <div>
            <label className="form-label">
              <Camera className="inline h-4 w-4 mr-1" />
              Donation photo (optional)
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Donation preview"
                  className="h-24 w-36 rounded-md object-cover border border-gray-200"
                />
              ) : (
                <div className="h-24 w-36 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
                  No photo
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={handlePhotoChange}
                className="block w-full text-sm text-gray-600 max-w-xs"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Upload a photo so volunteers and food banks can see what you’re donating.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline flex-1 sm:flex-none"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 sm:flex-none"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating Donation...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Create Donation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DonationListingForm
